import {
	faIcons,
	faPlus,
	faTimes,
	faTrash,
} from '@fortawesome/pro-solid-svg-icons';
import { validateValueMappings } from '@supply-mover-2-0/core';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '../../../components/base/Icon';
import { Text } from '../../../components/base/Text';
import { Card } from '../../../components/display/Card';
import type { TableColumn } from '../../../components/display/Table';
import { Table } from '../../../components/display/Table';
import { Input } from '../../../components/forms/Input';
import { ThemeAwareColorTiles } from '../../../components/forms/ThemeAwareColorTiles';
import {
	THEME_AWARE_COLORS,
	resolveThemeColor,
} from '../../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../../providers/ThemeProvider';
import { cn } from '../../../utils/cn';
import type { ColorValue, ValueMappingEntry } from '../../types';
import {
	IconDropdown,
	findIconByName,
} from '../list-configuration/columns/IconDropdown';

interface ColorPickerDropdownProps {
	onClose: () => void;
	onSelect: (colorId: string | null) => void;
	currentColor: string | null;
	triggerRef: React.RefObject<HTMLElement | null>;
	isModalOpen?: boolean;
}

function ColorPickerDropdown({
	onClose,
	onSelect,
	currentColor,
	triggerRef,
	isModalOpen = false,
}: ColorPickerDropdownProps) {
	const colors = useThemeColors();
	const { t } = useTranslation();
	const panelRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState({ top: 0, left: 0 });

	useEffect(() => {
		if (triggerRef.current == null) {
			return;
		}

		const rect = triggerRef.current.getBoundingClientRect();
		const scrollParent = triggerRef.current.closest('.overflow-y-auto');
		const scrollOffset = scrollParent?.scrollTop ?? 0;

		if (isModalOpen) {
			setPosition({
				top: rect.bottom + scrollOffset + 4,
				left: rect.left,
			});
		} else {
			setPosition({
				top: rect.bottom + window.scrollY + 4,
				left: rect.left + window.scrollX,
			});
		}
	}, [triggerRef, isModalOpen]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				panelRef.current != null &&
				!panelRef.current.contains(event.target as Node) &&
				triggerRef.current != null &&
				!triggerRef.current.contains(event.target as Node)
			) {
				onClose();
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [onClose, triggerRef]);

	const handleColorSelect = (colorId: string) => {
		onSelect(colorId);
		onClose();
	};

	return (
		<div
			ref={panelRef}
			className={cn(
				'absolute z-50 p-3 rounded-lg shadow-lg border',
				colors.classes.bgPrimary,
				colors.classes.borderDefault,
			)}
			style={{
				position: isModalOpen ? 'absolute' : 'fixed',
				top: position.top,
				left: position.left,
				minWidth: '220px',
			}}
		>
			<ThemeAwareColorTiles value={currentColor} onChange={handleColorSelect} />

			{currentColor != null && (
				<button
					type="button"
					onClick={() => {
						onSelect(null);
						onClose();
					}}
					className={cn(
						'flex items-center justify-center gap-1 w-full mt-2 py-1 text-xs rounded',
						colors.classes.textSecondary,
						colors.classes.bgSecondary,
						'hover:opacity-80',
					)}
				>
					<Icon icon={faTimes} size="xs" color={colors.values.textMuted} />
					{t('Clear')}
				</button>
			)}
		</div>
	);
}

interface ValueMappingTableProps {
	mappings: ValueMappingEntry[];
	onChange: (mappings: ValueMappingEntry[]) => void;
	isModalOpen?: boolean;
	displayStyle?: string;
}

export function ValueMappingTable({
	mappings,
	onChange,
	isModalOpen = false,
	displayStyle,
}: ValueMappingTableProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();
	const { t } = useTranslation();
	const [errors, setErrors] = useState<string[]>([]);
	const [showColorPicker, setShowColorPicker] = useState<{
		index: number;
		type: 'bg' | 'text' | 'border';
	} | null>(null);
	const [colorPickerRefs] = useState<{
		[key: string]: React.RefObject<HTMLButtonElement | null>;
	}>({});
	const [confirmDeleteIndex, setConfirmDeleteIndex] = useState<number | null>(
		null,
	);
	const [showIconPicker, setShowIconPicker] = useState<number | null>(null);
	const [iconPickerRefs] = useState<{
		[key: number]: React.RefObject<HTMLButtonElement | null>;
	}>({});

	// Validate on mount and when mappings change
	useEffect(() => {
		const validationErrors = validateValueMappings(mappings);
		setErrors(validationErrors);
	}, [mappings]);

	// Determine which color types to show based on display style
	const getColorTypesForDisplayStyle = (
		style?: string,
	): ('bg' | 'text' | 'border')[] => {
		if (!style) {
			return ['text'];
		}

		switch (style) {
			case 'badge':
			case 'pill':
			case 'chip':
				return ['bg', 'text', 'border'];
			case 'icon':
			case 'dot':
			case 'text':
			case 'email':
			case 'phone':
				return ['text'];
			default:
				return ['text'];
		}
	};

	const colorTypes = getColorTypesForDisplayStyle(displayStyle);
	const showMultipleColors = colorTypes.length > 1;

	const handleAddMapping = () => {
		const newMapping: ValueMappingEntry = {
			value: '',
			displayText: undefined,
			color: undefined,
		};
		onChange([...mappings, newMapping]);
	};

	const handleRemoveMapping = (index: number) => {
		const newMappings = mappings.filter((_, i) => i !== index);
		onChange(newMappings);
	};

	const handleUpdateMapping = (
		index: number,
		updates: Partial<ValueMappingEntry>,
	) => {
		const newMappings = [...mappings];
		const current = newMappings[index];
		if (current == null) {
			return;
		}
		newMappings[index] = { ...current, ...updates };
		onChange(newMappings);
	};

	const handleIconChange = (index: number, iconName: string) => {
		handleUpdateMapping(index, { iconName });
		setShowIconPicker(null);
	};

	const handleRemoveIcon = (index: number) => {
		handleUpdateMapping(index, { iconName: undefined });
	};

	const handleColorChange = (
		index: number,
		colorType: 'bg' | 'text' | 'border',
		colorString: string | null,
	) => {
		const currentColor = mappings[index]?.color ?? {};
		let newColor: ColorValue | undefined;

		if (colorString && colorString !== 'transparent') {
			newColor = { ...currentColor };

			if (showMultipleColors) {
				// For multi-color styles, update the specific color
				newColor[colorType] = colorString;
			} else {
				// For single-color styles, always update text
				newColor = { text: colorString };
			}
		} else if (
			colorType === 'bg' ||
			colorType === 'text' ||
			colorType === 'border'
		) {
			// Remove the specific color
			newColor = { ...currentColor };
			delete newColor[colorType];

			// If no colors left, set to undefined
			if (Object.keys(newColor).length === 0) {
				newColor = undefined;
			}
		}

		handleUpdateMapping(index, { color: newColor });
		setShowColorPicker(null);
	};

	// Create refs for color buttons
	const getColorButtonRef = (index: number, colorType: string) => {
		const key = `${index}-${colorType}`;
		if (!colorPickerRefs[key]) {
			colorPickerRefs[key] = React.createRef<HTMLButtonElement>();
		}
		return colorPickerRefs[key];
	};

	// Create refs for icon buttons
	const getIconButtonRef = (index: number) => {
		if (!iconPickerRefs[index]) {
			iconPickerRefs[index] = React.createRef<HTMLButtonElement>();
		}
		return iconPickerRefs[index];
	};

	const renderColorButton = (
		mapping: ValueMappingEntry,
		index: number,
		colorType: 'bg' | 'text' | 'border',
	) => {
		const buttonRef = getColorButtonRef(index, colorType);
		const colorValue = mapping.color?.[colorType];
		const resolvedColor = resolveThemeColor(colorValue, isDark);
		const colorName = THEME_AWARE_COLORS.find((c) => c.id === colorValue)?.name;

		return (
			<button
				ref={buttonRef}
				onClick={() => {
					setShowColorPicker(
						showColorPicker?.index === index &&
							showColorPicker?.type === colorType
							? null
							: { index, type: colorType },
					);
				}}
				className={cn(
					'flex items-center gap-2 px-3 py-2',
					colors.classes.bgPrimary,
					'border',
					colors.classes.borderDefault,
					'rounded-md',
					colors.classes.bgPrimaryHover,
					'min-w-[100px]',
				)}
			>
				<div
					className={cn(
						'w-4 h-4 border',
						colors.classes.borderDefault,
						'rounded',
					)}
					style={{
						backgroundColor:
							colorValue != null ? (resolvedColor ?? undefined) : 'transparent',
					}}
				/>
				<Text className={cn('text-xs', colors.classes.textPrimary)}>
					{colorName ?? (colorValue != null ? colorValue : t('Default'))}
				</Text>
			</button>
		);
	};

	const renderIconButton = (mapping: ValueMappingEntry, index: number) => {
		const buttonRef = getIconButtonRef(index);
		const iconName = mapping.iconName;
		const iconDefinition = iconName ? findIconByName(iconName) : null;

		return (
			<div className="flex items-center gap-2">
				<button
					ref={buttonRef}
					onClick={() => {
						setShowIconPicker(showIconPicker === index ? null : index);
					}}
					className={cn(
						'flex items-center gap-2 px-3 py-2',
						colors.classes.bgPrimary,
						'border',
						colors.classes.borderDefault,
						'rounded-md',
						colors.classes.bgPrimaryHover,
						'min-w-[100px]',
					)}
				>
					{iconDefinition ? (
						<>
							<Icon
								icon={iconDefinition}
								size="sm"
								color={colors.values.textPrimary}
							/>
							<Text className={cn('text-xs', colors.classes.textPrimary)}>
								{iconName}
							</Text>
						</>
					) : (
						<>
							<Icon icon={faIcons} size="sm" color={colors.values.textMuted} />
							<Text className={cn('text-xs', colors.classes.textSecondary)}>
								{t('Select Icon')}
							</Text>
						</>
					)}
				</button>
				{iconName && (
					<button
						onClick={() => {
							handleRemoveIcon(index);
						}}
						className={cn('p-1', colors.classes.bgPrimaryHover, 'rounded')}
					>
						<Icon icon={faTrash} size="xs" color={colors.values.textMuted} />
					</button>
				)}
			</div>
		);
	};

	// Define table columns
	const columns: TableColumn[] = [
		{
			key: 'originalValue',
			label: 'Original Value',
			width: 150,
			render: (_, item: ValueMappingEntry, index: number) => (
				<Input
					value={String(item.value)}
					onChange={(e) => {
						handleUpdateMapping(index, { value: e.target.value });
					}}
					placeholder="Enter value"
					className="w-full"
					size="sm"
				/>
			),
		},
		{
			key: 'displayText',
			label: 'Display Text',
			width: 150,
			render: (_, item: ValueMappingEntry, index: number) => (
				<Input
					value={item.displayText || ''}
					onChange={(e) => {
						handleUpdateMapping(index, {
							displayText: e.target.value || undefined,
						});
					}}
					placeholder="No change"
					className="w-full"
					size="sm"
				/>
			),
		},
		{
			key: 'icon',
			label: 'Icon',
			width: 150,
			render: (_, item: ValueMappingEntry, index: number) =>
				renderIconButton(item, index),
		},
		{
			key: 'colors',
			label: showMultipleColors ? 'Colors' : 'Color',
			width: showMultipleColors ? 350 : 120,
			render: (_, item: ValueMappingEntry, index: number) => (
				<div className={`${showMultipleColors ? 'flex gap-1' : ''} w-full`}>
					{showMultipleColors
						? colorTypes.map((colorType) => (
								<div key={colorType} className="flex-1">
									<Text
										className={cn(
											'text-[10px]',
											colors.classes.textSecondary,
											'mb-0.5 block',
										)}
									>
										{colorType === 'bg'
											? 'Background'
											: colorType === 'text'
												? 'Text'
												: 'Border'}
									</Text>
									{renderColorButton(item, index, colorType)}
								</div>
							))
						: renderColorButton(item, index, 'text')}
				</div>
			),
		},
		{
			key: 'actions',
			label: '',
			width: 50,
			align: 'center',
			render: (_, item: ValueMappingEntry, index: number) => (
				<div className="flex items-center justify-center">
					<button
						onClick={(e) => {
							e.stopPropagation();
							if (confirmDeleteIndex === index) {
								handleRemoveMapping(index);
								setConfirmDeleteIndex(null);
							} else {
								setConfirmDeleteIndex(index);
							}
						}}
						className={cn('p-2', colors.classes.bgPrimaryHover, 'rounded')}
						data-delete-button="true"
					>
						<Icon
							icon={faTrash}
							size="sm"
							color={
								confirmDeleteIndex === index
									? colors.values.danger
									: colors.values.textMuted
							}
						/>
					</button>
				</div>
			),
		},
	];

	// Add click handler to reset delete confirmation
	useEffect(() => {
		const handleClick = (e: MouseEvent) => {
			// Check if click is outside delete buttons
			const target = e.target as HTMLElement;
			if (!target.closest('[data-delete-button]')) {
				setConfirmDeleteIndex(null);
			}
		};

		document.addEventListener('click', handleClick);
		return () => {
			document.removeEventListener('click', handleClick);
		};
	}, []);

	return (
		<div className="flex flex-col">
			{/* Table */}
			<Table
				columns={columns}
				data={mappings}
				height={300}
				stickyHeader={false}
			/>

			{/* Add Button */}
			<button
				onClick={handleAddMapping}
				className={cn(
					'flex items-center justify-center w-full py-3',
					colors.classes.bgSecondary,
					'border-t',
					colors.classes.borderDefault,
					'rounded-b-md',
					colors.classes.bgPrimaryHover,
				)}
			>
				<Icon
					icon={faPlus}
					size="xs"
					color={colors.values.textMuted}
					className="mr-1.5"
				/>
				<Text className={cn('text-sm', colors.classes.textSecondary)}>
					Add Mapping
				</Text>
			</button>

			{/* Validation Errors */}
			{errors.length > 0 && (
				<Card className={cn('mt-2 p-2', colors.classes.bgErrorLight)}>
					{errors.map((error, index) => {
						// Create a stable key using error content and index
						const errorKey = `error_${error.slice(0, 20)}_${index}`;
						return (
							<Text
								key={errorKey}
								className={cn(
									'text-xs',
									colors.classes.textError,
									'mb-1 last:mb-0',
								)}
							>
								• {error}
							</Text>
						);
					})}
				</Card>
			)}

			{/* Color Picker */}
			{showColorPicker !== null && (
				<ColorPickerDropdown
					onClose={() => {
						setShowColorPicker(null);
					}}
					onSelect={(colorId) => {
						handleColorChange(
							showColorPicker.index,
							showColorPicker.type,
							colorId,
						);
					}}
					currentColor={
						mappings[showColorPicker.index]?.color?.[showColorPicker.type] ??
						null
					}
					triggerRef={
						getColorButtonRef(
							showColorPicker.index,
							showColorPicker.type,
						) as React.RefObject<HTMLElement>
					}
					isModalOpen={isModalOpen}
				/>
			)}

			{/* Icon Dropdown */}
			{showIconPicker !== null && (
				<IconDropdown
					visible={true}
					onClose={() => {
						setShowIconPicker(null);
					}}
					onSelect={(iconName) => {
						handleIconChange(showIconPicker, iconName);
					}}
					currentIcon={{ name: mappings[showIconPicker]?.iconName }}
					triggerRef={
						getIconButtonRef(showIconPicker) as React.RefObject<HTMLElement>
					}
					isModalOpen={isModalOpen}
				/>
			)}
		</div>
	);
}
